import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@app/core/app.config';
import { IdleSnackbarComponent } from '@app/shared/components/idle-snackbar/idle-snackbar.component';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { map, Observable, zipWith } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Service that watches the user's activity and detects when the app goes idle. The app is considered idle after not
 * detecting any user activity for a delay configured in {@link AppConfig}. As soon as the app goes idle, a timeout
 * countdown starts and is displayed in a snackbar. This timeout delay is configured in AppConfig. If user activity
 * resumes before the timeout countdown reaches 0, the idling state resets, the snackbar is dismissed and the service
 * waits for inactivity again. When the countdown reaches 0, the {@link timeout$} Observable emits and completes,
 * signaling the idle state has timed out and action should be taken.
 * A {@link ping$} Observable emits at regular interval (set in AppConfig) while user activity is detected and stops
 * emitting as soon as the app goes idle.
 */
@Injectable()
export default class IdleService {
  private idle = inject(Idle);
  private keepAlive = inject(Keepalive);
  private snackbarManager = inject(SnackbarManager);
  private appConfig = inject(AppConfig);

  constructor() {
    // Listen for user activity and detect when the app goes idle, then display countdown snackbar
    this.idle.setIdle(this.appConfig.IDLE_DELAY);
    this.idle.setTimeout(this.appConfig.IDLE_TIMEOUT);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.onIdleStart.pipe(
      map(() => this.snackbarManager.showGenericSnackbar(IdleSnackbarComponent)), // Display snackbar
      zipWith(this.idle.onIdleEnd), // Wait for user activity to resume...
      takeUntil(this.idle.onTimeout) // ...until idle state times out
    )
      .subscribe(([snackbar]) => snackbar.dismiss()); // Hide snackbar if idling ends

    // Emit ping events for as long as the user is active and until the app goes idle
    this.keepAlive.interval(this.appConfig.KEEPALIVE_PING_INTERVAL);
  }

  /**
   * Emits once then completes after the app idle state has timed out.
   */
  public get timeout$(): Observable<null> {
    return this.idle.onTimeout.pipe(map(() => null));
  }

  /**
   * Emits at regular interval for as long as the user is active. Stops emitting once the app goes idle.
   */
  public get ping$(): Observable<null> {
    return this.keepAlive.onPing.pipe(map(() => null));
  }

  /**
   * Start watching user activity to detect idling.
   */
  public watch(): void {
    this.idle.watch();
  }

  /**
   * Stop watching user activity.
   */
  public stop(): void {
    this.idle.stop();
  }
}
